import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  TableSortLabel,
  Link
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { useState, useMemo } from 'react'
import { type NewEnrollmentStatus } from 'core/types'
import * as XLSX from 'xlsx'
import { SpaceBeforeCapitals } from 'core/Utilities'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

export interface ProgramChoiceReportModel {
  childID: number
  firstName: string
  lastName: string
  dateOfBirth: string | null
  queueOrder: number | null
  program: string
  serviceProvider: string
  status: NewEnrollmentStatus
  enrollmentNumber: number
}

export interface ProgramChoiceReportTableViewProps {
  reportData: ProgramChoiceReportModel[]
}

type SortDirection = 'asc' | 'desc'
type SortableField = keyof ProgramChoiceReportModel

export function ProgramChoiceReportTableView (props: ProgramChoiceReportTableViewProps): JSX.Element {
  const { reportData } = props
  const [orderBy, setOrderBy] = useState<SortableField>('lastName')
  const [order, setOrder] = useState<SortDirection>('asc')
  const [orderGroup, setOrderGroup] = useState<number | null>(null)

  const formatDate = (dateString: string | null): string => {
    if (dateString == null) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }

  const maxGroup = Math.max(...reportData.map(o => o.enrollmentNumber))

  const handleRequestSort = (property: SortableField, group?: number): void => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    setOrderGroup(group ?? null)
  }

  const sortedData = useMemo(() => {
    const comparator = (a: ProgramChoiceReportModel, b: ProgramChoiceReportModel): number => {
      let aValue: any = a[orderBy]
      let bValue: any = b[orderBy]

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0
      if (aValue == null) return 1
      if (bValue == null) return -1

      // Convert dates to comparable values
      if (orderBy === 'dateOfBirth') {
        aValue = aValue != null ? new Date(aValue).getTime() : 0
        bValue = bValue != null ? new Date(bValue).getTime() : 0
      }

      // Group comparison
      if (orderGroup != null) {
        if (a.enrollmentNumber === orderGroup && b.enrollmentNumber !== orderGroup) {
          return -1
        } else if (a.enrollmentNumber !== orderGroup && b.enrollmentNumber === orderGroup) {
          return 1
        }
      }

      // String comparison (case-insensitive)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.toLowerCase().localeCompare(bValue.toLowerCase())
      }

      // Numeric comparison
      if (aValue < bValue) {
        return -1
      } else if (aValue > bValue) {
        return 1
      } else {
        return 0
      }
    }

    const idList = [...reportData].sort((a, b) => {
      return order === 'desc' ? -comparator(a, b) : comparator(a, b)
    }).map((item, index) => (item.childID))

    return new Set(idList) // adding items to a Set removes duplicates while preserving order
  }, [reportData, order, orderBy, orderGroup])

  const handleExportToExcel = (): void => {
    const headers = [
      'Last Name',
      'First Name',
      'Date of Birth',
      'Program'
    ]

    for (let i = 1; i <= maxGroup; i++) {
      headers.push(`Service Provider ${i}`)
      headers.push(`Status ${i}`)
    }

    const data: Array<Array<string | number | boolean>> = Array.from(sortedData).map(value => {
      const row = reportData.find(r => r.childID === value && r.enrollmentNumber === 1)
      if (row !== undefined) {
        const result: Array<string | number | boolean> = [
          row.firstName,
          row.lastName,
          formatDate(row.dateOfBirth),
          row.program ?? ''
        ]

        for (let i = 1; i <= maxGroup; i++) {
          const subrow = reportData.find(r => r.childID === value && r.enrollmentNumber === i)
          if (subrow !== undefined) {
            result.push(subrow.serviceProvider ?? '')
            result.push(SpaceBeforeCapitals(subrow.status.toString()))
          }
        }
        return result
      } else {
        return []
      }
    })

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data])
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Enrollment Details')

    XLSX.writeFile(workbook, `EnrollmentDetailsReport_${new Date().toISOString().split('T')[0]}.xlsx`)
  }

  if (reportData.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No records found matching the selected criteria.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 2 }}>
        <Chip
          label={`${sortedData.size} ${sortedData.size === 1 ? 'child' : 'children'}`}
          sx={{ bgcolor: '#E3F2FD', fontWeight: 'bold' }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleExportToExcel}
        >
          Download Excel
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: '70vh' }}>
        <Table stickyHeader size="small" className="report-table">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'lastName' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'lastName'}
                  direction={orderBy === 'lastName' ? order : 'asc'}
                  onClick={() => { handleRequestSort('lastName') }}
                >
                  Last Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'firstName' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'firstName'}
                  direction={orderBy === 'firstName' ? order : 'asc'}
                  onClick={() => { handleRequestSort('firstName') }}
                >
                  First Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'dateOfBirth' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'dateOfBirth'}
                  direction={orderBy === 'dateOfBirth' ? order : 'asc'}
                  onClick={() => { handleRequestSort('dateOfBirth') }}
                >
                  DOB
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'program' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'program'}
                  direction={orderBy === 'program' ? order : 'asc'}
                  onClick={() => { handleRequestSort('program') }}
                >
                  Program
                </TableSortLabel>
              </TableCell>
              { /* add a set of columns for each group */}
              {Array.from({ length: maxGroup }, (_, i) => {
                const group = i + 1
                return <>
                  <TableCell sortDirection={orderBy === 'serviceProvider' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'serviceProvider' && group === orderGroup}
                      direction={orderBy === 'serviceProvider' ? order : 'asc'}
                      onClick={() => { handleRequestSort('serviceProvider', group) }}
                    >
                      Service Provider {group}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'status' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'status' && group === orderGroup}
                      direction={orderBy === 'status' ? order : 'asc'}
                      onClick={() => { handleRequestSort('status', group) }}
                    >
                      Status {group}
                    </TableSortLabel>
                  </TableCell>
                </>
              })}

            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from(sortedData).map(value => {
              const row = reportData.find(r => r.childID === value && r.enrollmentNumber === 1)
              if (row === undefined) return null
              return <TableRow key={row.childID} hover>
                <TableCell>{row.lastName}</TableCell>
                <TableCell>
                  <Link
                    href={`/CareNetworkManagement/Details/${row.childID}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`View details for ${row.firstName} (opens in new window)`}
                    sx={{
                      color: '#367575',
                      fontWeight: 'medium',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {row.firstName}
                    <OpenInNewIcon sx={{ fontSize: '14px' }} />
                  </Link>
                </TableCell>
                <TableCell>{formatDate(row.dateOfBirth)}</TableCell>
                <TableCell>{row.program}</TableCell>
                {Array.from({ length: maxGroup }, (_, i) => {
                  const subrow = reportData.find(r => r.childID === value && r.enrollmentNumber === i + 1)
                  if (subrow === undefined) {
                    return <>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </>
                  } else {
                    return <>
                      <TableCell>{subrow.serviceProvider}</TableCell>
                      <TableCell>{SpaceBeforeCapitals(subrow.status.toString())}</TableCell>
                    </>
                  }
                })}
              </TableRow>
            })}

          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
